import { useState, useEffect, useRef } from "react";
import IconCard from '../../common/IconCard/IconCard';
import { Link } from "react-router-dom";
import CLINIC_CONFIG from '../../../config/clinicConfig';
import CardHeader from '../../common/CardHeader/CardHeader';

const AffordablePart = () => {
  const [lottieData, setLottieData] = useState<{
    healthInsurance: object | null;
    insurance: object | null;
    creditCard: object | null;
    finance: object | null;
    healthCheck: object | null;
    financeAnimation: object | null;
    percentageInsurance: object | null;
    heartbeat: object | null;
  }>({
    healthInsurance: null,
    insurance: null,
    creditCard: null,
    finance: null,
    healthCheck: null,
    financeAnimation: null,
    percentageInsurance: null,
    heartbeat: null,
  });

  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Only start loading Lotties when section approaches viewport
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); io.disconnect(); } },
      { rootMargin: '300px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    // Load Lottie JSON files only after section is near viewport
    const loadLottieFiles = async () => {
      try {
        const [
          healthInsurance,
          insurance,
          creditCard,
          finance,
          healthCheck,
          financeAnimation,
          percentageInsurance,
          heartbeat,
        ] = await Promise.all([
          fetch('/homepagelottiejson/health insurance.json').then(res => res.json()),
          fetch('/homepagelottiejson/insurance.json').then(res => res.json()),
          fetch('/homepagelottiejson/Credit card.json').then(res => res.json()),
          fetch('/homepagelottiejson/Finance.json').then(res => res.json()),
          fetch('/homepagelottiejson/Health Check.json').then(res => res.json()),
          fetch('/homepagelottiejson/Finance Animation.json').then(res => res.json()),
          fetch('/homepagelottiejson/percentageinsurance.json').then(res => res.json()),
          fetch('/homepagelottiejson/heartbeat.json').then(res => res.json()),
        ]);
        setLottieData({
          healthInsurance,
          insurance,
          creditCard,
          finance,
          healthCheck,
          financeAnimation,
          percentageInsurance,
          heartbeat,
        });
      } catch (error) {
        console.error('Error loading Lottie files:', error);
      }
    };
    loadLottieFiles();
  }, [isVisible]);

  /* ================= AFFORDABLE : 4 BOXES ================= */
  const affordableCards = [
    {
      lottieData: lottieData.healthInsurance,
      heading: "Medicaid & Medicare",
      link: "/medmedicaid",
      children: (
        <p>
          For affordable care we accept <br />
          <Link
            to="/medicaid#medicaid-section"
            className="fw-bold text-decoration-none text-dark"
          >
            Medicaid
          </Link>{" "}
          and{" "}
          <Link
            to="/medicare#medicare-section"
            className="fw-bold text-decoration-none text-dark"
          >
            Medicare
          </Link>{" "}
          for <br />
          adults and children.
        </p>
      ),
    },
    {
      lottieData: lottieData.insurance,
      heading: "Insurance",
      link: "/insurance",
      children: (
        <p>
          We work with most insurance <br />
          providers including{" "}
          <Link
            to="/delta-dental#DeltaDental-section"
            className="fw-bold text-decoration-none text-dark"
            onClick={(e) => e.stopPropagation()}
          >
            Delta Dental,
          </Link>{" "}
          <Link
            to="/blue-cross-blue-shield#BlueCrossBlueShield-section"
            className="fw-bold text-decoration-none text-dark"
            onClick={(e) => e.stopPropagation()}
          >
            BCBS
          </Link>
          , &{" "}
          <Link
            to="/ppo#ConnectionDental-section"
            className="fw-bold text-decoration-none text-dark"
            onClick={(e) => e.stopPropagation()}
          >
            Connection Dental!
          </Link>
        </p>
      ),
    },
    {
      lottieData: lottieData.creditCard,
      heading: "Membership",
      link: CLINIC_CONFIG.membership,
      target: "_blank",
      rel: "noopener noreferrer",
      children: (
        <p>
          Our membership plans provide easy access to <br />
          quality dental care. <br />
          <a
            href={CLINIC_CONFIG.membership}
            target="_blank"
            rel="noopener noreferrer"
            className="fw-bold rule-link"
          >
            Sign Up Now!
          </a>
        </p>
      ),
    },
    {
      lottieData: lottieData.financeAnimation,
      heading: "Financing",
      link: "/financing",
      children: (
        <p>
          Flexible financing options available <br />
          like{" "}
          <Link
            to="/in-house-payment-plan#InHousePaymentPlan-section"
            className="fw-bold text-decoration-none text-dark"
            onClick={(e) => e.stopPropagation()}
          >
            In-House Payment
          </Link>{" "}
          Plan and <br />
          <Link
            to="/care-credit#CareCredit-section"
            className="fw-bold text-decoration-none text-dark"
            onClick={(e) => e.stopPropagation()}
          >
            CareCredit
          </Link>
        </p>
      ),
    },
  ];

  /* ================= TRANSPARENCY : 4 BOXES ================= */
  const transparencyCards = [
    {
      lottieData: lottieData.healthCheck,
      heading: "Clear Treatment Options",
      children: (
        <p>
          We explain all treatment options clearly so you can make informed
          decisions.
        </p>
      ),
    },
    {
      lottieData: lottieData.finance,
      heading: "Upfront Pricing",
      children: (
        <p>
          No hidden fees — you'll know exactly what you'll pay before treatment
          begins.
        </p>
      ),
    },
    {
      lottieData: lottieData.percentageInsurance,
      heading: "Insurance Clarity",
      children: (
        <p>
          We review your insurance benefits so you know exactly what is covered.
        </p>
      ),
    },
    {
      lottieData: lottieData.heartbeat,
      heading: "Only What You Need",
      children: (
        <p>
          We only recommend treatments that are truly necessary for your health.
        </p>
      ),
    },
  ];

  return (
    <>
      {/* ================= AFFORDABLE SECTION ================= */}
      <section ref={sectionRef} className="container my-5 home-glass-section">
        <CardHeader>AFFORDABLE</CardHeader>
        <div className="row g-4 justify-content-center">
          {affordableCards.map((card, index) => (
            <div key={index} className="col-12 col-md-6 col-lg-3">
              <IconCard {...card} />
            </div>
          ))}
        </div>
      </section>

      {/* ================= TRANSPARENCY SECTION ================= */}
      <section className="container my-5 home-glass-section">
        <CardHeader>TRANSPARENCY</CardHeader>
        <div className="row g-4 justify-content-center">
          {transparencyCards.map((card, index) => (
            <div key={index} className="col-12 col-md-6 col-lg-3">
              <IconCard {...card} />
            </div>
          ))}
        </div>
      </section>


    </>
  );
};

export default AffordablePart;
